import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { Column } from '@tanstack/react-table'
import React from 'react'
import { Label } from '@/components/ui/label'

export interface DimensionFilterOptions {
  value: string
  label: string
}

interface DimensionColumnFilterProps<TData> {
  column: Column<TData, unknown>
  openFilter: boolean
  setOpenFilter: SetState<boolean>
  filterOptions: DimensionFilterOptions[]
}

export function DimensionColumnFilter<TData>({
  column,
  setOpenFilter,
  filterOptions,
}: DimensionColumnFilterProps<TData>) {
  const filterValue = React.useMemo(
    () => (column.getFilterValue() ?? '') as string,
    [column.getFilterValue()],
  )

  const [selectedStatuses, setSelectedStatuses] = React.useState<string[]>(() => {
    return filterValue ? (filterValue?.split(',') as string[]) : []
  })

  const handleKeyboardInteraction = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      action()
    }
  }

  const isAllSelected = React.useMemo(
    () => selectedStatuses?.length === filterOptions.length,
    [selectedStatuses],
  )
  const hasChanges = React.useMemo(() => {
    return selectedStatuses.length > 0 && selectedStatuses.join(',') !== column.getFilterValue()
  }, [selectedStatuses, filterValue])

  const onSelectAll = React.useCallback((checked: boolean) => {
    setSelectedStatuses(checked ? filterOptions.map((option) => option.value) : [])
  }, [])

  const onApplyFilter = React.useCallback(() => {
    column.setFilterValue(selectedStatuses.join(','))
    setOpenFilter(false)
  }, [selectedStatuses])

  const onClearFilter = React.useCallback(() => {
    setSelectedStatuses([])
    column.setFilterValue('')
    setOpenFilter(false)
  }, [])

  const onStatusToggle = React.useCallback((value: string) => {
    setSelectedStatuses((prevFilters) =>
      prevFilters.includes(value)
        ? prevFilters.filter((filter) => filter !== value)
        : [...prevFilters, value],
    )
  }, [])

  return (
    <div className='flex w-[240px] flex-col gap-1 p-1.5'>
      {/* Select All Toggle */}
      <div className='flex items-center gap-4 px-1 py-3'>
        <Checkbox
          id='selectAll'
          checked={isAllSelected}
          onCheckedChange={onSelectAll}
          variant='neutral'
        />
        <Label variant={'checkbox'} htmlFor='selectAll' className='w-full cursor-pointer text-sm'>
          Select All
        </Label>
      </div>

      {/* Status Filter Options */}
      <div className='max-h-[200px] overflow-auto'>
        {filterOptions.map((option) => (
          <div
            key={option.value}
            role='button'
            tabIndex={0}
            onKeyDown={(e) => handleKeyboardInteraction(e, () => onStatusToggle(option.value))}
            onClick={() => onStatusToggle(option.value)}
            className={cn(
              'flex cursor-pointer items-center gap-4 rounded px-5 py-3',
              selectedStatuses?.includes(option.value) && 'bg-neutral-50',
            )}
          >
            <Checkbox checked={selectedStatuses.includes(option.value)} variant='neutral' />
            {option.label}
          </div>
        ))}
      </div>

      {/* Filter Actions */}
      <div className='flex items-center justify-end gap-3 px-1 py-2'>
        <Button
          size={'xs'}
          onClick={onClearFilter}
          disabled={selectedStatuses.length === 0}
          className='disabled:opacity-50'
          unstyled
        >
          Reset
        </Button>
        <Button size='xs' onClick={onApplyFilter} disabled={!hasChanges} className='normal-case'>
          Apply Filters
        </Button>
      </div>
    </div>
  )
}
